import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { ColorMatch } from '~js/components/color-match';

export class Swatch {
  constructor(element) {
    this.element = element;
    this.swatchLink = this.element.nextElementSibling;
    this.colorString = element.getAttribute(a.attributes$E.swatch);
    this.image = this.element.getAttribute(a.attributes$E.image);
    this.imageId = this.element.getAttribute(a.attributes$E.imageId);
    this.variant = this.element.getAttribute(a.attributes$E.variant);
    this.outer = this.element.closest(s.selectors$X.productBlock);
    this.hoverImages = [];

    const matcher = new ColorMatch({ color: this.colorString });
    matcher.getColor().then((result) => {
      this.colorMatch = result;
      this.init();
    });
  }

  init() {
    this.setStyles();

    if (this.variant && this.outer) {
      this.handleClicks();
    }

    if (!this.image && this.swatchLink) {
      this.swatchLink.classList.add(c.classes$N.noImage);
    }
  }

  setStyles() {
    if (this.colorMatch && this.colorMatch.hex) {
      this.element.style.setProperty('--swatch', `${this.colorMatch.hex}`);
    }

    if (this.colorMatch && this.colorMatch.path) {
      this.element.style.setProperty('background-image', `url(${this.colorMatch.path})`);
    }
  }

  handleClicks() {
    // Change PGI featured image on swatch click
    this.swatchLink.addEventListener('click', (event) => {
      const isFocusEnabled = !document.body.classList.contains(c.classes$N.noOutline);
      const variantId = this.swatchLink.getAttribute(a.attributes$E.variant);

      if (!isFocusEnabled) {
        event.preventDefault();
        this.updateImagesAndLinksOnEvent(variantId);
      }
    });

    this.swatchLink.addEventListener('keyup', (event) => {
      const isFocusEnabled = !document.body.classList.contains(c.classes$N.noOutline);
      const variantId = this.swatchLink.getAttribute(a.attributes$E.variant);

      if (event.code !== theme.keyboardKeys.ENTER && event.code !== theme.keyboardKeys.NUMPADENTER) {
        return;
      }

      if (!isFocusEnabled) {
        event.preventDefault();
        this.swatchLink.dispatchEvent(new Event('mouseenter', { bubbles: true }));
        this.updateImagesAndLinksOnEvent(variantId);
      }
    });
  }

  updateImagesAndLinksOnEvent(variantId) {
    this.updateLinks();
    this.replaceImages(variantId);
  }

  updateLinks() {
    this.linkElements = this.outer.querySelectorAll(s.selectors$X.link);
    this.quickView = this.outer.querySelector(s.selectors$X.quickView);

    // Update links
    if (this.linkElements.length) {
      this.linkElements.forEach((element) => {
        const destination = getUrlWithVariant(element.getAttribute('href'), this.variant);
        element.setAttribute('href', destination);
      });
    }

    // Change quickview variant with swatch one
    if (this.quickView && theme.settings.quickBuy === 'quick_buy') {
      this.quickView.setAttribute(a.attributes$E.variantId, this.variant);
    }
  }

  replaceImages(id) {
    const imageSecondary = this.outer.querySelector(`[${a.attributes$E.variantSecondaryId}="${id}"]`);
    const gridImage = this.outer.querySelector(`[${a.attributes$E.variantId}="${id}"]`);
    const gridImages = this.outer.querySelectorAll(s.selectors$X.gridImage);
    const currentGridImage = [...gridImages].find((image) => image.classList.contains(c.classes$N.mediaVisible));

    // Add new loaded image and sync with the secondary image for smooth animation
    if (gridImage && this.imageId) {
      if (!imageSecondary || !currentGridImage) return;

      const onAnimationEnd = () => {
        requestAnimationFrame(() => {
          currentGridImage.classList.remove(c.classes$N.mediaVisible);
          gridImage.classList.add(c.classes$N.mediaVisible);

          requestAnimationFrame(() => {
            imageSecondary.classList.remove(c.classes$N.mediaVisible);
          });
        });

        imageSecondary.removeEventListener('animationend', onAnimationEnd);
      };

      requestAnimationFrame(() => {
        imageSecondary.classList.add(c.classes$N.mediaVisible);
      });

      imageSecondary.addEventListener('animationend', onAnimationEnd);
    }

    // Change all hover images classes
    if (theme.settings.productGridHover === 'image') {
      this.hoverImages = this.outer.querySelectorAll(s.selectors$X.productImageHover);
    }

    if (this.hoverImages.length > 1) {
      this.hoverImages.forEach((hoverImage) => {
        hoverImage.classList.remove(c.classes$N.mediaHoverVisible);

        if (hoverImage.getAttribute(a.attributes$E.variantId) === this.variant) {
          hoverImage.classList.add(c.classes$N.mediaHoverVisible);
        } else {
          this.hoverImages[0].classList.add(c.classes$N.mediaHoverVisible);
        }
      });
    }
  }
}

  /**
   * Returns a URL with a variant ID query parameter. Useful for updating window.history
   * with a new URL based on the currently select product variant.
   * @param {string} url - The URL you wish to append the variant ID to
   * @param {number} id  - The variant ID you wish to append to the URL
   * @returns {string} - The new url which includes the variant ID query parameter
   */

  function getUrlWithVariant(url, id) {
    if (/variant=/.test(url)) {
      return url.replace(/(variant=)[^&]+/, '$1' + id);
    } else if (/\?/.test(url)) {
      return url.concat('&variant=').concat(id);
    }

    return url.concat('?variant=').concat(id);
  }